import { graphql, type GraphQLTaggedNode } from 'react-relay';

import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { timeGuard } from '@confluence/query-preloader-tools/entry-points/timeGuard.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

import type {
	preloadOutgoingLinksQuery$data,
	preloadOutgoingLinksQuery$variables,
} from './__generated__/preloadOutgoingLinksQuery.graphql.ts';

export type OutgoingLinksQueryData = preloadOutgoingLinksQuery$data;
export type OutgoingLinksQueryVariables = preloadOutgoingLinksQuery$variables;

const PRELOAD_TIMEOUT_MS = 500;
const TIMED_OUT = Symbol('outgoing-links-preload-timed-out');

type QueryResult = Awaited<ReturnType<typeof runQuery>>;

export const OutgoingLinksQuery: GraphQLTaggedNode = graphql`
	query preloadOutgoingLinksQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				outgoingLinks {
					externalOutgoingLinks(first: 50) {
						nodes {
							url
						}
					}
				}
			}
		}
	}
`;

const runQuery = (contentId: string) =>
	query<preloadOutgoingLinksQuery$data, preloadOutgoingLinksQuery$variables>({
		query: OutgoingLinksQuery,
		variables: { contentId },
	});

const reportUnavailable = (reason: 'query-error' | 'timeout', cause?: unknown): void => {
	ssrPrepareErrorsClient.submitError(
		new Error(`Outgoing links preload unavailable: ${reason}`, { cause }),
		{ operation: 'preload-outgoing-links', reason },
	);

	if (cause instanceof Error) {
		markErrorAsHandled(cause);
	}
};

/**
 * Warms the external `outgoingLinks` query so its result is in the cache before hydration. The
 * consumer reads it back through the same query. A timeout or an error leaves the cache unwarmed,
 * and the consumer renders the unchanged first-party card.
 *
 * Gate the push on the experiment at the route, so only the cohort runs it.
 */
export const preloadOutgoingLinks = async (contentId: string): Promise<void> => {
	try {
		const promise = runQuery(contentId);
		const result = await timeGuard<QueryResult | typeof TIMED_OUT>(
			() => promise,
			TIMED_OUT,
			PRELOAD_TIMEOUT_MS,
		);

		if (result === TIMED_OUT) {
			reportUnavailable('timeout');
			return;
		}

		if (result.hasErrors || result.errors?.length) {
			reportUnavailable('query-error', result.errors?.[0]);
		}
	} catch (cause) {
		reportUnavailable('query-error', cause);
	}
};
